import {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
  createApi,
  fetchBaseQuery,
} from '@reduxjs/toolkit/query/react';
import { RootState } from '../../store';
import { getMcpBaseUrl, handleErrorResponse } from '../api';
import mcpApiEndPoints from './mcpApiConstants';
import { normalizeMcpCapabilityName } from './mcpUtils';
import type {
  AdvisorAssignmentsListRequest,
  AdvisorAssignmentsListResponse,
  CatalogTool,
  CapabilityAssignment,
  CapabilityBinding,
  CapabilityBindingGetRequest,
  CapabilityBindingGetResponse,
  McpCatalogToolsResponse,
  McpServer,
  McpServersResponse,
  SetAdvisorAssignmentsRequest,
  SetAdvisorAssignmentsResponse,
  ToggleMcpServerRequest,
  TenantBindingsListRequest,
  TenantBindingsListResponse,
  TenantTool,
  TenantToolsListResponse,
  ToggleMcpRequest,
  ToggleMcpResponse,
  ToggleTenantToolRequest,
  UpdateMcpToolRequest,
  UpsertMcpToolRequest,
  UpsertActionToolResponse,
} from './mcptypes';

const mcpPrepareHeaders = (headers: Headers, { getState }: { getState: () => unknown }) => {
  const token = (getState() as RootState)?.auth?.token;
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  return headers;
};

const mcpInnerBaseQuery = fetchBaseQuery({
  baseUrl: '/',
  prepareHeaders: mcpPrepareHeaders,
});

const mcpDynamicBaseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions
) => {
  const host = getMcpBaseUrl();
  if (!host) {
    return {
      error: {
        status: 'CUSTOM_ERROR',
        error: 'MCP base URL is not configured. Set CTY_MCP_API_URL in the environment.',
      } as FetchBaseQueryError,
    };
  }
  const path = typeof args === 'string' ? args : args.url;
  let url: string;
  if (path.startsWith('http')) {
    url = path;
  } else if (path.startsWith('/')) {
    url = `${host}${path}`;
  } else {
    url = `${host}/${path}`;
  }
  const adjusted = typeof args === 'string' ? url : { ...args, url };
  return mcpInnerBaseQuery(adjusted, api, extraOptions);
};

const mcpHandleErrorResponse = (response: FetchBaseQueryError & { data?: { error?: string } }) => {
  if (response?.data?.error) {
    return {
      message: String(response.data.error),
      status: response.status,
    };
  }
  return handleErrorResponse(response);
};

export const mcpApiSlice = createApi({
  reducerPath: 'mcpApi',
  baseQuery: mcpDynamicBaseQuery,
  tagTypes: ['McpTenantTools', 'McpAdvisorAssignments', 'McpCatalogTools', 'McpCapabilityBinding'],
  endpoints: (builder) => ({
    getMcpServers: builder.query<McpServer[], void>({
      query: () => mcpApiEndPoints.mcpServers,
      transformResponse: (response: McpServersResponse) => response.servers ?? [],
      transformErrorResponse: mcpHandleErrorResponse,
    }),
    getMcpCatalogTools: builder.query<CatalogTool[], void>({
      query: () => mcpApiEndPoints.toolsCatalog,
      transformResponse: (response: McpCatalogToolsResponse) => response.tools ?? [],
      transformErrorResponse: mcpHandleErrorResponse,
      providesTags: [{ type: 'McpCatalogTools', id: 'LIST' }],
    }),
    getTenantTools: builder.query<TenantTool[], string>({
      query: (tenantId) => ({
        url: mcpApiEndPoints.tenantToolsList,
        method: 'POST',
        body: { tenant_id: tenantId, scope: 'tenant', visibility: 'all' },
      }),
      transformResponse: (response: TenantToolsListResponse) => response.tools ?? [],
      transformErrorResponse: mcpHandleErrorResponse,
      providesTags: (_result, _error, tenantId) => [
        { type: 'McpTenantTools', id: tenantId },
      ],
    }),
    toggleTenantTool: builder.mutation<void, ToggleTenantToolRequest>({
      query: ({ tenantId, capabilityId, enabled }) => ({
        url: mcpApiEndPoints.tenantToolsToggle,
        method: 'POST',
        body: {
          tenant_id: tenantId,
          capability_id: capabilityId,
          enabled,
        },
      }),
      transformErrorResponse: mcpHandleErrorResponse,
      invalidatesTags: (_result, _error, { tenantId }) => [
        { type: 'McpTenantTools', id: tenantId },
      ],
    }),
    getTenantBindings: builder.query<CapabilityBinding[], TenantBindingsListRequest>({
      query: ({ tenantId, advisorId }) => ({
        url: mcpApiEndPoints.bindingsList,
        method: 'POST',
        body: advisorId
          ? { tenant_id: tenantId, advisor_id: advisorId }
          : { tenant_id: tenantId },
      }),
      transformResponse: (response: TenantBindingsListResponse) => response.bindings ?? [],
      transformErrorResponse: mcpHandleErrorResponse,
    }),
    getCapabilityBinding: builder.query<CapabilityBindingGetResponse | null, CapabilityBindingGetRequest>({
      async queryFn({ tenantId, capabilityName, advisorId }, _api, _extraOptions, baseQuery) {
        const result = await baseQuery({
          url: mcpApiEndPoints.bindingsGet,
          method: 'POST',
          body: {
            tenant_id: tenantId,
            capability_name: capabilityName,
            ...(advisorId ? { advisor_id: advisorId } : {}),
          },
        });
        if (result.error) {
          if (result.error.status === 404) {
            return { data: null };
          }
          return { error: result.error };
        }
        const response = result.data as CapabilityBindingGetResponse;
        return { data: response ?? null };
      },
      providesTags: (_result, _error, { tenantId, capabilityName, advisorId }) => [
        {
          type: 'McpCapabilityBinding',
          id: `${tenantId}:${capabilityName}${advisorId ? `:${advisorId}` : ''}`,
        },
      ],
    }),
    getAdvisorAssignments: builder.query<CapabilityAssignment[], AdvisorAssignmentsListRequest>({
      query: ({ tenantId, advisorId }) => ({
        url: mcpApiEndPoints.assignmentsList,
        method: 'POST',
        body: { tenant_id: tenantId, advisor_id: advisorId },
      }),
      transformResponse: (response: AdvisorAssignmentsListResponse) => response.assignments ?? [],
      transformErrorResponse: mcpHandleErrorResponse,
      providesTags: (_result, _error, { advisorId }) => [
        { type: 'McpAdvisorAssignments', id: advisorId },
      ],
    }),
    upsertActionTool: builder.mutation<UpsertActionToolResponse, UpsertMcpToolRequest>({
      query: ({ tenantId: _tenantId, ...body }) => ({
        url: mcpApiEndPoints.tenantToolsUpsert,
        method: 'POST',
        body,
      }),
      transformErrorResponse: mcpHandleErrorResponse,
      invalidatesTags: (_result, _error, { tenantId, name, advisor_id: advisorId }) => {
        const capabilityKey = tenantId ? normalizeMcpCapabilityName(name) : '';
        const bindingTags: { type: 'McpCapabilityBinding'; id: string }[] = [];
        if (tenantId && capabilityKey) {
          bindingTags.push({ type: 'McpCapabilityBinding', id: `${tenantId}:${capabilityKey}` });
          if (advisorId) {
            bindingTags.push({
              type: 'McpCapabilityBinding',
              id: `${tenantId}:${capabilityKey}:${advisorId}`,
            });
          }
        }
        return [
          ...(tenantId ? [{ type: 'McpTenantTools' as const, id: tenantId }] : []),
          ...bindingTags,
        ];
      },
    }),
    updateMcpTool: builder.mutation<UpsertActionToolResponse, UpdateMcpToolRequest>({
      query: ({ tenantId: _tenantId, ...body }) => ({
        url: mcpApiEndPoints.toolsUpdate,
        method: 'POST',
        body,
      }),
      transformErrorResponse: mcpHandleErrorResponse,
      invalidatesTags: (_result, _error, { tenantId, capability_name }) => [
        ...(tenantId ? [{ type: 'McpTenantTools' as const, id: tenantId }] : []),
        ...(tenantId
          ? [{ type: 'McpCapabilityBinding' as const, id: `${tenantId}:${capability_name}` }]
          : []),
      ],
    }),
    setAdvisorAssignments: builder.mutation<
      SetAdvisorAssignmentsResponse,
      SetAdvisorAssignmentsRequest
    >({
      query: ({ tenantId, advisorId, capabilityIds }) => ({
        url: mcpApiEndPoints.assignmentsSet,
        method: 'POST',
        body: {
          tenant_id: tenantId,
          advisor_id: advisorId,
          capability_ids: capabilityIds,
        },
      }),
      transformErrorResponse: mcpHandleErrorResponse,
      invalidatesTags: (_result, _error, { advisorId }) => [
        { type: 'McpAdvisorAssignments', id: advisorId },
      ],
    }),
    toggleMcpServer: builder.mutation<void, ToggleMcpServerRequest>({
      query: (body) => ({
        url: mcpApiEndPoints.serversToggle,
        method: 'POST',
        body,
      }),
      transformErrorResponse: mcpHandleErrorResponse,
    }),
    toggleMcp: builder.mutation<ToggleMcpResponse, ToggleMcpRequest>({
      query: ({ active }) => ({
        url: mcpApiEndPoints.mcpToggle,
        method: 'POST',
        body: { active },
      }),
      transformErrorResponse: mcpHandleErrorResponse,
    }),
  }),
});

export const {
  useGetMcpServersQuery,
  useGetMcpCatalogToolsQuery,
  useGetTenantToolsQuery,
  useLazyGetTenantToolsQuery,
  useToggleTenantToolMutation,
  useUpsertActionToolMutation,
  useUpdateMcpToolMutation,
  useGetTenantBindingsQuery,
  useGetCapabilityBindingQuery,
  useGetAdvisorAssignmentsQuery,
  useSetAdvisorAssignmentsMutation,
  useToggleMcpServerMutation,
  useToggleMcpMutation,
} = mcpApiSlice;
